import { GATE_LABELS, isGate, type WfStep } from './workflowModel';
import { yesAnswerDependency, yesAnswerStepKey } from '../../office/workflowAnswerGate';

export type FlowNode =
  | { id: string; kind: 'step'; index: number; row: number }
  | { id: string; kind: 'gate'; key: string; label: string; row: number }
  | { id: string; kind: 'answer'; questionIndex: number; branch: 'yes' | 'no'; row: number };
export type FlowEdge = { from: string; to: string };
export type FlowLayout = { rows: FlowNode[][]; edges: FlowEdge[]; looped: number[] };

const stepId = (i: number) => `s${i}`;
const gateId = (key: string) => `g:${key}`;
const answerId = (key: string, branch: 'yes' | 'no') => `a:${branch}:${key}`;

/**
 * Top-to-bottom placement: a step sits one row below the last step it waits for, so steps that can
 * run at the same time share a row. Gates sit one row above the first step they hold back. Steps
 * caught in a loop (A waits for B, B waits for A) cannot be placed and are returned in `looped`.
 */
export function layoutFlow(steps: WfStep[]): FlowLayout {
  const byKey = new Map(steps.map((s, i) => [s.key, i]));
  const local = (s: WfStep) => s.dependsOnSteps.flatMap((dependency) => {
    const answerKey = yesAnswerStepKey(dependency);
    const index = byKey.get(answerKey || dependency);
    return index == null ? [] : [{ index, distance: answerKey ? 2 : 1 }];
  });
  const answerQuestions = new Set<string>();
  steps.forEach((step) => step.dependsOnSteps.forEach((dependency) => {
    const key = yesAnswerStepKey(dependency);
    const index = key ? byKey.get(key) : undefined;
    if (key && index != null && steps[index].actionType === 'toggle_yes_no') answerQuestions.add(key);
  }));
  const level = new Array<number>(steps.length).fill(0);
  const state = new Array<0 | 1 | 2>(steps.length).fill(0);
  const looped = new Set<number>();

  const visit = (i: number): number => {
    if (state[i] === 2) return level[i];
    if (state[i] === 1) {
      looped.add(i);
      return -1;
    }
    state[i] = 1;
    let lv = 1;
    for (const d of local(steps[i])) {
      const dl = visit(d.index);
      if (dl < 0) {
        looped.add(i);
        lv = -1;
        break;
      }
      lv = Math.max(lv, dl + d.distance);
    }
    state[i] = 2;
    level[i] = looped.has(i) ? -1 : lv;
    return level[i];
  };
  steps.forEach((_, i) => visit(i));

  // Gates go one row above the earliest step that waits for them (row 0 is for gates only).
  const gateRow = new Map<string, number>();
  steps.forEach((s, i) => {
    if (level[i] < 0) return;
    s.dependsOnSteps.filter((dependency) => isGate(dependency) && !answerQuestions.has(yesAnswerStepKey(dependency) || '')).forEach((g) => gateRow.set(g, Math.min(gateRow.get(g) ?? Infinity, level[i] - 1)));
  });

  const rowsMap = new Map<number, FlowNode[]>();
  const put = (row: number, n: FlowNode) => {
    if (!rowsMap.has(row)) rowsMap.set(row, []);
    rowsMap.get(row)!.push(n);
  };
  gateRow.forEach((row, key) => {
    const answerKey = yesAnswerStepKey(key);
    const label = answerKey ? `Yes to ${steps.find((step) => step.key === answerKey)?.title || answerKey}` : GATE_LABELS[key] || key;
    put(row, { id: gateId(key), kind: 'gate', key, label, row });
  });
  steps.forEach((_, i) => level[i] >= 0 && put(level[i], { id: stepId(i), kind: 'step', index: i, row: level[i] }));
  answerQuestions.forEach((key) => {
    const questionIndex = byKey.get(key)!;
    if (level[questionIndex] < 0) return;
    const row = level[questionIndex] + 1;
    put(row, { id: answerId(key, 'yes'), kind: 'answer', questionIndex, branch: 'yes', row });
    put(row, { id: answerId(key, 'no'), kind: 'answer', questionIndex, branch: 'no', row });
  });
  const sortKey = (node: FlowNode) => node.kind === 'gate' ? -1 : node.kind === 'answer' ? node.questionIndex * 2 + (node.branch === 'yes' ? 0 : 1) : node.index * 2;
  const rows = Array.from(rowsMap.keys())
    .sort((a, b) => a - b)
    .map((r) => rowsMap.get(r)!.sort((a, b) => sortKey(a) - sortKey(b)));

  const edges: FlowEdge[] = [];
  answerQuestions.forEach((key) => {
    const questionIndex = byKey.get(key)!;
    if (level[questionIndex] < 0) return;
    edges.push({ from: stepId(questionIndex), to: answerId(key, 'yes') });
    edges.push({ from: stepId(questionIndex), to: answerId(key, 'no') });
  });
  steps.forEach((s, i) => {
    if (level[i] < 0) return;
    s.dependsOnSteps.forEach((d) => {
      const answerKey = yesAnswerStepKey(d);
      if (answerKey && answerQuestions.has(answerKey)) {
        const hasConditionalParent = s.dependsOnSteps.some((dep) => {
          const parentIndex = byKey.get(dep);
          return parentIndex != null && steps[parentIndex].dependsOnSteps.includes(yesAnswerDependency(answerKey));
        });
        if (!hasConditionalParent) edges.push({ from: answerId(answerKey, 'yes'), to: stepId(i) });
      } else if (isGate(d)) edges.push({ from: gateId(d), to: stepId(i) });
      else if (answerQuestions.has(d) && s.dependsOnSteps.includes(yesAnswerDependency(d))) return;
      else if (byKey.has(d) && level[byKey.get(d)!] >= 0) edges.push({ from: stepId(byKey.get(d)!), to: stepId(i) });
    });
  });
  return { rows, edges, looped: Array.from(looped).sort((a, b) => a - b) };
}

/** Everything a node waits for (all the way up) and everything it unblocks (all the way down). */
export function chainOf(edges: FlowEdge[], id: string): { up: Set<string>; down: Set<string> } {
  const walk = (dir: 'up' | 'down') => {
    const seen = new Set<string>();
    const stack = [id];
    while (stack.length) {
      const cur = stack.pop()!;
      for (const e of edges) {
        const n = dir === 'up' ? (e.to === cur ? e.from : null) : e.from === cur ? e.to : null;
        if (n && !seen.has(n)) {
          seen.add(n);
          stack.push(n);
        }
      }
    }
    return seen;
  };
  return { up: walk('up'), down: walk('down') };
}
